// Type definitions for Fountain Editor V2

import type { IEntityTag, TextAtomContentJson } from '@/types';

export interface Scene {
    id: number;
    episode_id: number;
    fragment_id?: number; // Links to parent Fragment (Act in film mode)
    title: string;
    content: string; // Raw Fountain format text
    act?: number; // Act number (1, 2, 3, etc.)
    number?: number; // Scene number within episode
    position?: number; // Raw backend position (1000-scaled) used for ordering / inserts
    /** Scene code from backend («{fragment}-{n}»). `null` on a DRAFTED scene —
     *  it has no number at all (#1840-05). */
    code?: string | null;
    /** The backend's `scenes.is_draft`, carried under its wire name like every
     *  other server-owned field here. What the SERVER currently believes: the
     *  engine's own draft flags are the live truth while the editor is open,
     *  and this is the value the flag sync diffs against (#1840-06). */
    is_draft?: boolean;
    version: number; // For optimistic locking
    tags: IEntityTag[]; // Character tags for the scene
    created_at: string;
    updated_at: string;
    textAtomId?: number; // ID of the primary text_atom for saving
    loglineTextAtomId?: number; // ID of the scene_logline text_atom for saving logline
    contentJson?: TextAtomContentJson; // Structured data (line associations, etc.) from primary text_atom
    synopsisTextAtomId?: number; // ID of the scene_synopsis text_atom for saving the PLOT line (#1835)
}

// Fountain block types (from parser)
export enum FountainBlockType {
    SCENE_HEADING = 'SCENE_HEADING',
    LOGLINE = 'LOGLINE', // First line after scene heading - scene summary/description
    PLOT = 'PLOT', // Second metadata line after the logline — the scene's «Сюжет» (issue #1835)
    ACTION = 'ACTION',
    CHARACTER = 'CHARACTER',
    DIALOGUE = 'DIALOGUE',
    PARENTHETICAL = 'PARENTHETICAL',
    TRANSITION = 'TRANSITION',
    NOTE = 'NOTE',
    SYNOPSIS = 'SYNOPSIS',
    SECTION = 'SECTION',
    PAGE_BREAK = 'PAGE_BREAK',
    DUAL_DIALOGUE = 'DUAL_DIALOGUE',
    CENTERED = 'CENTERED',
    LYRIC = 'LYRIC',
}

// Parsed Fountain block
export interface FountainBlock {
    type: FountainBlockType;
    text: string;
    line: number; // Line number in original content
    offset?: number; // Character offset in original content
    length?: number; // Length of the block
    raw?: string; // Original raw text before parsing
    /**
     * Only meaningful for PARENTHETICAL blocks. Disambiguates action-remark
     * (`'action'`) from dialogue-remark (`'speaker'`). Set explicitly when the
     * source line has a `!(...)` "force action-context" prefix; absent
     * otherwise (consumers fall back to walk-up classification).
     */
    parentheticalContext?: 'action' | 'speaker';
}

// Parse error
export interface ParseError {
    message: string;
    line: number;
    offset: number;
    severity: 'error';
}

// Parse warning
export interface ParseWarning {
    message: string;
    line: number;
    offset: number;
    severity: 'warning';
}

// Parse result with errors and warnings
export interface ParseResult {
    blocks: FountainBlock[];
    errors: ParseError[];
    warnings: ParseWarning[];
}

// ============================================================================
// Composite block model — the editor-internal shape where ONE block may span
// several source lines: a «Реплика» (a run of DIALOGUE lines) or an ACTION run
// (ADR 0008). The «Персонаж» cue is a block of its own; what makes it and the
// replica below it a pair is their adjacency, nothing else (ADR 0020). The wire
// format is unchanged Fountain.
// ============================================================================

export type SubField =
    | 'text' // ACTION.text, TRANSITION text, LOGLINE
    | 'character' // DIALOG.character (cue)
    | 'phrase' // DIALOG.phrase (one entry per phrase line)
    | 'intExt'
    | 'location'
    | 'sublocation'
    | 'title'
    | 'timeOfDay';

export interface CompositeBlock {
    // Every block type except PARENTHETICAL, which is not a block: a whole-line
    // `(beat)` is a row of the «Реплика» it sits in.
    type: Exclude<FountainBlockType, FountainBlockType.PARENTHETICAL>;
    /** The block's rows, joined by `\n`. Multi-row blocks are a «Реплика» (a run
     *  of DIALOGUE lines) and an ACTION run (ADR 0008); every other type is one
     *  row. Consumers must not assume one FlatLine per block — use the per-line
     *  `isBlockLeading` / `isBlockTrailing` flags or `flatLinesByBlockId`. */
    text: string;
    line: number; // starting line in original Fountain text (-1 if synthetic)
    raw?: string; // contiguous source range, joined by '\n'
}

export interface CompositeParseResult {
    blocks: CompositeBlock[];
    errors: ParseError[];
    warnings: ParseWarning[];
}

// Full parsed Fountain document (with title page metadata)
export interface ParsedFountain {
    blocks: FountainBlock[];
    title?: string;
    credit?: string;
    author?: string[];
    source?: string;
    notes?: string;
    draft_date?: string;
    date?: string;
    contact?: string;
    copyright?: string;
}
